import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchStore, deleteStore, fetchStoreStats, updateStore } from '../services/api';

export default function StoreDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showApiKey, setShowApiKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: storeData, isLoading: storeLoading } = useQuery({
    queryKey: ['store', id],
    queryFn: () => fetchStore(id!),
    enabled: !!id,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['store-stats', id],
    queryFn: () => fetchStoreStats(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; domain?: string }) => updateStore(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', id] });
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteStore(id!),
    onSuccess: () => {
      navigate('/stores');
    },
  });

  const handleCopyApiKey = () => {
    if (storeData?.store.api_key) {
      navigator.clipboard.writeText(storeData.store.api_key);
      alert('API Key copied to clipboard!');
    }
  };

  const handleUpdate = () => {
    updateMutation.mutate({
      name,
      domain: domain || undefined,
    });
  };

  const handleStartEdit = () => {
    if (storeData?.store) {
      setName(storeData.store.name);
      setDomain(storeData.store.domain || '');
      setIsEditing(true);
    }
  };

  if (storeLoading || statsLoading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  if (!storeData?.store) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">
          <p className="text-gray-600">Store not found</p>
          <Link to="/stores" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
            ← Back to Stores
          </Link>
        </div>
      </div>
    );
  }

  const store = storeData.store;
  const stats = statsData || {
    totalQuestions: 0,
    answeredQuestions: 0,
    avgResponseTime: 0,
    conversionRate: 0,
    recentQuestions: [],
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <Link to="/stores" className="text-blue-600 hover:text-blue-700">
          ← Back to Stores
        </Link>
      </div>

      {/* Store Info */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Store Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">{store.name}</h2>
                {store.domain && <p className="text-gray-600">{store.domain}</p>}
                <p className="text-sm text-gray-500 mt-2">
                  Created {new Date(store.created_at).toLocaleDateString()}
                </p>
              </>
            )}
          </div>
          {!isEditing && (
            <div className="flex gap-2">
              <button
                onClick={handleStartEdit}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        {/* API Key */}
        {!isEditing && (
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
            <div className="flex gap-2">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={store.api_key}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
              <button
                onClick={() => setShowApiKey(!showApiKey)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {showApiKey ? '🙈 Hide' : '👁️ Show'}
              </button>
              <button
                onClick={handleCopyApiKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                📋 Copy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        <Link
          to={`/stores/${id}/products`}
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition block"
        >
          <div className="text-3xl mb-2">📦</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Manage Products</h3>
          <p className="text-sm text-gray-600">Add and manage your product catalog</p>
        </Link>
        <Link
          to={`/stores/${id}/customize`}
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition block"
        >
          <div className="text-3xl mb-2">🎨</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Customize Widget</h3>
          <p className="text-sm text-gray-600">Change colors, position, and messages</p>
        </Link>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-3xl mb-2">📦</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Installation</h3>
          <p className="text-sm text-gray-600 mb-3">Add widget to your website</p>
          <div className="bg-gray-50 p-3 rounded text-xs font-mono overflow-x-auto">
            {`<script src="https://widget.buychatly.com/widget.js" data-api-key="${store.api_key}"></script>`}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <p className="text-sm text-gray-600">Total Questions</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalQuestions}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Answered</p>
            <p className="text-2xl font-bold text-gray-900">{stats.answeredQuestions}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Avg Response</p>
            <p className="text-2xl font-bold text-gray-900">{stats.avgResponseTime}s</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Conversion</p>
            <p className="text-2xl font-bold text-gray-900">{stats.conversionRate}%</p>
          </div>
        </div>

        {stats.recentQuestions.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Recent Questions</h4>
            <div className="space-y-3">
              {stats.recentQuestions.slice(0, 5).map((q: any, i: number) => (
                <div key={i} className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-gray-900">{q.question}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(q.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Delete Store?</h3>
            <p className="text-gray-600 mb-6">
              This will permanently delete "{store.name}" and all associated data. This action cannot be
              undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Store'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
